<template>
  <v-container fluid>
    <section class="ps-products">
      <KpiCards class="mb-3" :items="kpiItems" :loading="!summaries.length" :skeleton="5">
        <template #extra="{ item }">
          <div v-if="item.currency_breakdown" class="ps-kpi-currency ml-auto">
            <CurrencyBreakdown :breakdown="item.currency_breakdown" />
          </div>
        </template>
      </KpiCards>

      <v-card class="ps-card-soft ps-table-card">
        <v-card-text>
          <div class="d-flex align-center flex-wrap ps-table-head">
            <span class="ps-title-tick"></span>
            <h1 class="ps-table-title">{{ $t(filter) }}</h1>
            <span v-if="pageMeta.total" class="ps-count-note">{{
              countNote
            }}</span>
            <v-spacer />
            <v-btn-toggle
              v-model="status_filter"
              mandatory
              dense
              class="ps-status-toggle"
            >
              <v-btn
                v-for="(val, i) in status_list"
                :key="i"
                small
                :value="val.value"
              >
                {{ $t(val.text) }}
                <span class="ps-status-badge">{{
                  statusCount(val.value)
                }}</span>
              </v-btn>
            </v-btn-toggle>
          </div>

          <div class="d-flex align-center flex-wrap ps-filter-bar">
            <div class="ps-search-wrap">
              <TextSearch
                v-model="search"
                @search="handleSearch"
                @onClear="
                  search = '';
                  handleSearch();
                "
              />
            </div>
            <div class="ps-select-wrap">
              <TextSelect
                :dense="true"
                :placeholder="false"
                :items="categories"
                v-model="category_id"
                text="category_display"
                itemValue="id"
              />
            </div>
            <div class="ps-select-wrap">
              <TextSelect
                :dense="true"
                :placeholder="false"
                :items="filters"
                v-model="filter"
                :returnObject="false"
                :text="localeKey"
                itemValue="id"
              />
            </div>
            <div class="ps-select-wrap">
              <TextSelect
                :dense="true"
                :placeholder="false"
                :items="sortOptions"
                v-model="sortOption"
                :returnObject="true"
                :text="localeKey"
                itemValue="id"
              />
            </div>
            <span
              v-if="isFiltered"
              class="ps-clear-filters"
              @click="resetFilters"
            >
              {{ $t("clear_filters") }}
            </span>
            <v-spacer />
            <div class="d-flex align-center ps-action-group">
              <v-btn
                v-if="user?.stores?.product_soft_delete === 1"
                min-width="auto"
                height="40"
                class="mr-2 ps-icon-btn"
                depressed
                outlined
                to="/trash-products"
              >
                <v-icon small>fal fa-trash</v-icon>
              </v-btn>
              <v-btn
                min-width="auto"
                height="40"
                class="mr-2 ps-export-btn"
                depressed
                outlined
                @click="exportToExcel"
              >
                {{ $t("export_excel") }}
              </v-btn>
              <v-btn
                min-width="auto"
                height="40"
                depressed
                class="ps-new-btn"
                to="/manage-add-product"
              >
                <v-icon left small>fal fa-plus-circle</v-icon>
                {{ $t("add_new_product") }}
              </v-btn>
            </div>
          </div>

          <div class="ps-table-wrap">
            <div class="ps-grid-row ps-head-row">
              <span
                class="ps-th ps-th--sortable"
                @click="sortByColumn('id')"
              >
                {{ $t("id") }}
                <span class="ps-th-arrow">{{ columnArrow("id") }}</span>
              </span>
              <span class="ps-th">{{ $t("image") }}</span>
              <span
                class="ps-th ps-th--sortable"
                @click="sortByColumn('name')"
              >
                {{ $t("product_name") }}
                <span class="ps-th-arrow">{{ columnArrow("name") }}</span>
              </span>
              <span class="ps-th">{{ $t("category") }}</span>
              <span
                class="ps-th ps-th--sortable"
                @click="sortByColumn('stock')"
              >
                {{ $t("stock") }}
                <span class="ps-th-arrow">{{ columnArrow("stock") }}</span>
              </span>
              <span class="ps-th ps-th--right">{{ $t("cost") }}</span>
              <span
                class="ps-th ps-th--right ps-th--sortable"
                @click="sortByColumn('price_sale')"
              >
                {{ $t("price") }}
                <span class="ps-th-arrow">{{ columnArrow("price_sale") }}</span>
              </span>
              <span class="ps-th ps-th--center">{{ $t("status") }}</span>
              <span
                class="ps-th ps-th--right ps-th--sortable"
                @click="sortByColumn('created_at')"
              >
                {{ $t("date") }}
                <span class="ps-th-arrow">{{ columnArrow("created_at") }}</span>
              </span>
              <span class="ps-th ps-th--right">{{ $t("manage") }}</span>
            </div>

            <div
              v-for="(item, index) in tableRows"
              :key="item.id"
              class="ps-grid-row ps-row"
              :class="{ 'is-inactive': !item.is_active }"
            >
              <span class="ps-row-index">{{ index + 1 }}</span>
              <span class="ps-row-thumb">
                <v-avatar tile size="32" class="rounded-lg ps-thumb-avatar">
                  <app-img :img="item.img" />
                </v-avatar>
                <span
                  v-if="item.product_type"
                  class="ps-thumb-dot"
                  :style="{ background: getProductTypeColor(item.product_type) }"
                ></span>
              </span>
              <div class="ps-row-name">
                <span class="ps-product-name">{{
                  getProductName(item) | nameLimit
                }}</span>
                <span v-if="item.code" class="ps-product-code">
                  {{ item.code }}
                  <button
                    type="button"
                    class="ps-copy-code"
                    :aria-label="$t('copy')"
                    @click.stop="copyBarcode(item.code)"
                  >
                    <v-icon x-small>fal fa-copy</v-icon>
                  </button>
                </span>
              </div>
              <span class="ps-category-pill">{{
                getCategoryName(item.category)
              }}</span>
              <div class="ps-row-stock">
                <div class="d-flex align-baseline" style="gap: 6px">
                  <span
                    class="ps-stock-value"
                    :class="'ps-stock-value--' + stockHealth(item).tone"
                    >{{ productSize(item) }}</span
                  >
                  <span class="ps-stock-note">{{
                    stockHealth(item).label
                  }}</span>
                </div>
                <span class="ps-stock-track">
                  <span
                    class="ps-stock-fill"
                    :class="'ps-stock-fill--' + stockHealth(item).tone"
                    :style="{ width: stockBarWidth(item) }"
                  ></span>
                </span>
              </div>
              <span class="ps-cost">{{
                priceWithCurrency(item.price_buy, item.currency)
              }}</span>
              <div class="ps-row-price">
                <span class="ps-price-value">{{
                  priceWithCurrency(item.price_sale, item.currency)
                }}</span>
                <span v-if="rowMargin(item)" class="ps-margin-note">{{
                  rowMargin(item)
                }}</span>
              </div>
              <span class="ps-row-status">
                <v-switch
                  :input-value="item.is_active"
                  @change="(value) => proStatus(item, { desiredStatus: value })"
                  :color="item.is_active ? 'primary' : 'red'"
                  inset
                  dense
                  hide-details
                  class="ma-0 pa-0 ps-switch"
                />
              </span>
              <span class="ps-row-date">{{ item.created_at | created_at }}</span>
              <span class="ps-row-actions">
                <span class="ps-act" title="View" @click="viewItem(item)">
                  <v-icon x-small>fal fa-eye</v-icon>
                </span>
                <span
                  v-if="MyRole('edit_product')"
                  class="ps-act"
                  title="Edit"
                  @click="editItem(item)"
                >
                  <v-icon x-small>fal fa-edit</v-icon>
                </span>
                <span
                  v-if="MyRole('delete_product')"
                  class="ps-act ps-act--danger"
                  title="Delete"
                  @click="deleteItem(item)"
                >
                  <v-icon x-small>fal fa-trash</v-icon>
                </span>
              </span>
            </div>

            <div v-if="!tableRows.length && !overlay" class="ps-empty">
              <v-icon class="ps-empty-icon">fal fa-search</v-icon>
              <span class="ps-empty-text">{{ $t("no_data") }}</span>
              <span
                v-if="isFiltered"
                class="ps-clear-filters"
                @click="resetFilters"
              >
                {{ $t("clear_filters") }}
              </span>
            </div>
          </div>

          <div class="d-flex align-center flex-wrap ps-pagination">
            <span class="ps-page-note">{{ pageRangeNote }}</span>
            <v-spacer />
            <div class="d-flex align-center ps-per-page">
              <span>{{ $t("rows_per_page") }}</span>
              <div class="ps-per-page-select">
                <select v-model.number="perPage" @change="onPerPageChange">
                  <option v-for="n in perPageOptions" :key="n" :value="n">
                    {{ n }}
                  </option>
                </select>
              </div>
            </div>
            <div class="d-flex align-center ps-pager">
              <span
                class="ps-pager-btn"
                :class="{ 'is-disabled': pageMeta.current_page <= 1 }"
                @click="prevPage"
              >
                <v-icon x-small>fal fa-chevron-left</v-icon>
              </span>
              <span
                v-for="n in pageNumbers"
                :key="n"
                class="ps-pager-num"
                :class="{ 'is-active': n === pageMeta.current_page }"
                @click="goToPage(n)"
              >
                {{ n }}
              </span>
              <span
                class="ps-pager-btn"
                :class="{ 'is-disabled': pageMeta.current_page >= pageMeta.last_page }"
                @click="nextPage"
              >
                <v-icon x-small>fal fa-chevron-right</v-icon>
              </span>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <app-custom-dialog
        v-model="dialog"
        :footer="false"
        title="description"
        width="750"
        @submit="submitForm"
      >
        <div slot="activator">
          <AppDetail :item="form" />
        </div>
      </app-custom-dialog>
      <app-loading :overlay="overlay" />
    </section>
  </v-container>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import AppDetail from "@/components/products/details.vue";
import { myMixin } from "@/mixin/main";
import {
  success,
  confirm,
  errorAlert,
  error,
  toast,
} from "@/controllers/alert";
import TextSelect from "@/components/global/TextSelect";
import CurrencyBreakdown from "@/components/global/CurrencyBreakdown.vue";

export default {
  mixins: [myMixin],
  data: () => ({
    search: "",

    filter: "all_product",
    status_filter: null,
    status_list: [
      {
        value: null,
        text: "all",
      },
      {
        value: 1,
        text: "active",
      },
      {
        value: 0,
        text: "inactive",
      },
    ],
    category_id: {
      id: 0,
      la_name: "ທັງໝົດ",
      en_name: "All",
    },
    update: false,
    dialogAdd: false,
    dialog: false,
    form: {},
    summaries: [],
    sortOption: null,
    page: 1,
    perPage: 30,
    perPageOptions: [10, 30, 50],
  }),
  async created() {
    this.sortOption = this.sortOptions[0];
    await Promise.all([
      this.fetchSummary(),
      this.fetchItem(),
      this.fetchCategory(),
    ]);
  },
  watch: {
    category_id(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.search = "";
        this.page = 1;
        Promise.all([this.fetchItem(), this.fetchSummary()]);
      }
    },
    filter(val, oldVal) {
      if (val !== oldVal) {
        this.search = "";
        this.page = 1;
        Promise.all([this.fetchItem(), this.fetchSummary()]);
      }
    },
    status_filter() {
      this.page = 1;
      Promise.all([this.fetchItem(), this.fetchSummary()]);
    },
    sortOption(val, old) {
      // old === null is created() assigning the default; it already fetches
      if (val !== old && old !== null) {
        this.page = 1;
        this.fetchItem();
      }
    },
  },
  components: {
    AppDetail,
    TextSelect,
    CurrencyBreakdown,
  },
  computed: {
    kpiItems() {
      return this.summaries.map((item, index) => ({
        key: item.title,
        title: item.title,
        icon: item.icon,
        value: Number(item.total || 0),
        note: item.description ? this.$t(item.description) : "",
        // only the 3rd card carries the multi-currency breakdown
        currency_breakdown: index === 2 ? item.currency_breakdown : null,
      }));
    },
    ...mapGetters({
      overlay: "index/overlay",
      category: "index/category",
      allProducts: "index/allProducts",
    }),

    categoryLocaleKey() {
      const local = this.$i18n.locale;
      if (local === "en") return "en_name";
      if (local === "th") return "th_name";
      if (local === "cn" || local === "ch") return "cn_name";
      return "la_name";
    },
    filters() {
      return [
        {
          id: "all_product",
          name_en: "All Product",
          name_la: "ສິນຄ້າທັງໝົດ",
          name_th: "สินค้าทั้งหมด",
          name_cn: "所有产品",
        },
        {
          id: "almost_out_of_stock",
          name_en: "Notify Stock",
          name_la: "ສິນຄ້າໃກ້ຫມົດ",
          name_th: "แจ้งเตือนสต็อก",
          name_cn: "库存通知",
        },
        {
          id: "in_stock",
          name_en: "In Stock",
          name_la: "ມີສະຕ໋ອກ",
          name_th: "มีสต็อก",
          name_cn: "有库存",
        },
        {
          id: "out_of_stock",
          name_en: "Out of Stock",
          name_la: "ໝົດສະຕ໋ອກ",
          name_th: "สินค้าหมด",
          name_cn: "缺货",
        },
        {
          id: "best_sale",
          name_en: "Best Sale",
          name_la: "ສິນຄ້າຂາຍດີ",
          name_th: "สินค้าขายดี",
          name_cn: "热销产品",
        },
        {
          id: "expired_product",
          name_en: "Expiry Date Products",
          name_la: "ສິນຄ້າມີວັນໝົດອາຍຸ",
          name_th: "สินค้ามีวันหมดอายุ",
          name_cn: "有过期日期的产品",
        },
      ];
    },

    categories() {
      if (!this.category?.length) {
        return [];
      }
      const all = {
        id: 0,
        la_name: "ທັງໝົດ",
        en_name: "All",
        th_name: "ทั้งหมด",
        cn_name: "全部",
      };
      return this.mapCategoriesForSelect([all, ...this.category]);
    },
    sortOptions() {
      return [
        {
          id: "default",
          sort_by: "number",
          sort: "asc",
          name_en: "Default order",
          name_la: "ລຳດັບປົກກະຕິ",
          name_th: "เรียงตามปกติ",
          name_cn: "默认排序",
        },
        {
          id: "id_asc",
          sort_by: "id",
          sort: "asc",
          name_en: "ID, low to high",
          name_la: "ໄອດີ ນ້ອຍ→ໃຫຍ່",
          name_th: "ไอดี น้อย→มาก",
          name_cn: "ID 从低到高",
        },
        {
          id: "id_desc",
          sort_by: "id",
          sort: "desc",
          name_en: "ID, high to low",
          name_la: "ໄອດີ ໃຫຍ່→ນ້ອຍ",
          name_th: "ไอดี มาก→น้อย",
          name_cn: "ID 从高到低",
        },
        {
          id: "name_asc",
          sort_by: "name",
          sort: "asc",
          name_en: "Name, A to Z",
          name_la: "ຊື່, ກ-ຮ",
          name_th: "ชื่อ, ก-ฮ",
          name_cn: "名称，A-Z",
        },
        {
          id: "name_desc",
          sort_by: "name",
          sort: "desc",
          name_en: "Name, Z to A",
          name_la: "ຊື່, ຮ-ກ",
          name_th: "ชื่อ, ฮ-ก",
          name_cn: "名称，Z-A",
        },
        {
          id: "stock_desc",
          sort_by: "stock",
          sort: "desc",
          name_en: "Stock, high to low",
          name_la: "ສະຕ໋ອກ ຫຼາຍ→ໜ້ອຍ",
          name_th: "สต็อก มาก→น้อย",
          name_cn: "库存从高到低",
        },
        {
          id: "stock_asc",
          sort_by: "stock",
          sort: "asc",
          name_en: "Stock, low to high",
          name_la: "ສະຕ໋ອກ ໜ້ອຍ→ຫຼາຍ",
          name_th: "สต็อก น้อย→มาก",
          name_cn: "库存从低到高",
        },
        {
          id: "price_desc",
          sort_by: "price_sale",
          sort: "desc",
          name_en: "Price, high to low",
          name_la: "ລາຄາ ສູງ→ຕ່ຳ",
          name_th: "ราคา สูง→ต่ำ",
          name_cn: "价格从高到低",
        },
        {
          id: "price_asc",
          sort_by: "price_sale",
          sort: "asc",
          name_en: "Price, low to high",
          name_la: "ລາຄາ ຕ່ຳ→ສູງ",
          name_th: "ราคา ต่ำ→สูง",
          name_cn: "价格从低到高",
        },
        {
          id: "newest",
          sort_by: "created_at",
          sort: "desc",
          name_en: "Newest first",
          name_la: "ໃໝ່ສຸດກ່ອນ",
          name_th: "ใหม่สุดก่อน",
          name_cn: "最新优先",
        },
        {
          id: "oldest",
          sort_by: "created_at",
          sort: "asc",
          name_en: "Oldest first",
          name_la: "ເກົ່າສຸດກ່ອນ",
          name_th: "เก่าสุดก่อน",
          name_cn: "最旧优先",
        },
      ];
    },
    isFiltered() {
      return !!(
        this.search ||
        (this.category_id && this.category_id.id !== 0) ||
        this.filter !== "all_product" ||
        this.status_filter !== null ||
        (this.sortOption && this.sortOption.id !== "default")
      );
    },

    tableRows() {
      const p = this.allProducts;
      if (p && !Array.isArray(p) && Array.isArray(p.data)) return p.data;
      return Array.isArray(p) ? p : [];
    },
    pageMeta() {
      const p = this.allProducts;
      if (p && !Array.isArray(p) && typeof p === "object") {
        return {
          current_page: p.current_page || 1,
          last_page: p.last_page || 1,
          total: p.total || 0,
          from: p.from || 0,
          to: p.to || 0,
        };
      }
      const len = Array.isArray(p) ? p.length : 0;
      return { current_page: 1, last_page: 1, total: len, from: len ? 1 : 0, to: len };
    },
    pageNumbers() {
      return Array.from({ length: this.pageMeta.last_page }, (_, i) => i + 1);
    },
    countNote() {
      return `${this.$options.filters.NumberFormat(this.pageMeta.total)} ${this.$t(
        "products"
      )}`;
    },
    pageRangeNote() {
      if (!this.pageMeta.total) return this.$t("no_data");
      return `${this.pageMeta.from}–${this.pageMeta.to} / ${this.pageMeta.total}`;
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
      UPDATE_PRODUCT_IN_LIST: "index/UPDATE_PRODUCT_IN_LIST",
    }),
    async fetchCategory() {
      const body = {
        url: "categories",
        that: this,
        commit: "SET_CATEGORY",
      };
      await this.fetch(body);
    },
    buildFilterParams() {
      const params = {
        cat_id: this.category_id?.id ?? 0,
        q: this.search || "",
        filter: this.filter || "all_product",
      };

      if (this.status_filter !== null && this.status_filter !== undefined) {
        params.is_active = this.status_filter;
      }

      return params;
    },
    async fetchItem() {
      this.SET_OVERLAY(true);
      const statusParam =
        this.status_filter !== null ? `&is_active=${this.status_filter}` : "";
      const sortBy = this.sortOption?.sort_by || "number";
      const sortDir = this.sortOption?.sort || "asc";
      const body = {
        that: this,
        url: `products_all?cat_id=${this.category_id.id}&q=${this.search}&filter=${this.filter}${statusParam}&sort_by=${sortBy}&sort=${sortDir}&page=${this.page}&per_page=${this.perPage}`,
        commit: "SET_ALL_PRODUCTS",
      };
      await this.fetch(body);
      this.SET_OVERLAY(false);
    },

    async newItem() {
      await Help.clearForm(this.form, this);
      await this.fetchItem();
      this.update = false;
      this.dialogAdd = !this.dialogAdd;
    },
    editItem({ id }) {
      this.$router.push(`/manage-edit-product/${id}`);
    },
    async fetchSummary() {
      try {
        const params = this.buildFilterParams();
        const { data } = await this.$http.get(`/get_summary`, {
          params,
        });
        if (data.status) {
          this.summaries = data.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    statusCount(value) {
      const all = this.summaries[0];
      if (!all) return 0;
      if (value === 1) return all.active_count ?? 0;
      if (value === 0) return all.inactive_count ?? 0;
      return all.all_count ?? 0;
    },

    handleSearch() {
      this.page = 1;
      Promise.all([this.fetchItem(), this.fetchSummary()]);
    },

    resetFilters() {
      this.search = "";
      this.filter = "all_product";
      this.category_id = { id: 0, la_name: "ທັງໝົດ", en_name: "All" };
      this.status_filter = null;
      this.sortOption = this.sortOptions[0];
      this.page = 1;
    },


    async exportToExcel() {
      try {
        this.SET_OVERLAY(true);
        const params = this.buildFilterParams();
        const query = new URLSearchParams(params).toString();
        const url = query ? `products_export?${query}` : "products_export";

        const response = await this.$http.get(url, {
          responseType: "blob",
        });
        const blob = response?.data;
        if (!blob) {
          error(this, "excel_export_failed");
          return;
        }
        const date = new Date().toISOString().slice(0, 10);
        const blobUrl = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", `products_${date}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        toast(this, "excel_export_success");
      } catch (err) {
        console.error("Error exporting Excel:", err);
        const msg =
          err?.response?.data?.message || err?.message || "excel_export_failed";
        error(this, typeof msg === "string" ? msg : "excel_export_failed");
      } finally {
        this.SET_OVERLAY(false);
      }
    },
    async copyBarcode(code) {
      if (!code || !navigator.clipboard) return;
      try {
        await navigator.clipboard.writeText(String(code));
        toast(this, "copied_to_clipboard");
      } catch (err) {
        error(this, "failed_to_copy");
      }
    },

    async deleteItem({ id }) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (isConfirmed) {
        try {
          const { data } = await this.$http.delete(`products/${id}`);
          if (data.status) {
            await Help.clearForm(this.form, this);
            await this.fetchItem();
            await success(this, data.message);
          } else {
            error(this, data?.message || "error_occurred");
          }
        } catch (err) {
          const msg =
            err?.response?.data?.message || err?.message || "error_occurred";
          error(this, typeof msg === "string" ? msg : "error_occurred");
        }
      }
    },
    async viewItem({ id }) {
      // PosSmart v2: dedicated product detail page (kept copy from v2/possmart-vue)
      this.$router.push(`/product-detail/${id}`);
    },
    async submitForm() {
      var result;
      try {
        if (this.update) {
          result = await this.$http.post(
            `product_size/${this.form.id}`,
            this.form
          );
        } else {
          result = await this.$http.post("product_size", this.form);
        }
        if (result.data.status) {
          this.dialogAdd = !this.dialogAdd;
          await Help.clearForm(this.form, this);
          await this.fetchItem();
          await success(this, result.data.message);
        }
      } catch (error) {
        console.log("error", error);
      }
    },

    async proStatus(val, options = {}) {
      const { desiredStatus = null } = options || {};
      const previousStatus =
        typeof desiredStatus === "boolean" ? !desiredStatus : val.is_active;

      try {
        const { data } = await this.$http.put(`product_active/${val.id}`);
        if (data.status) {
          const updatedProduct =
            data.data ||
            Object.assign({}, val, {
              is_active:
                typeof desiredStatus === "boolean"
                  ? desiredStatus
                  : !previousStatus,
            });
          this.UPDATE_PRODUCT_IN_LIST(updatedProduct);
          await success(this, data.message);
        } else {
          if (typeof previousStatus === "boolean") {
            this.UPDATE_PRODUCT_IN_LIST({
              id: val.id,
              is_active: previousStatus,
            });
          }
          await errorAlert(this, data.message);
        }
      } catch (error) {
        if (typeof previousStatus === "boolean") {
          this.UPDATE_PRODUCT_IN_LIST({
            id: val.id,
            is_active: previousStatus,
          });
        }
        console.log(error);
      }
    },

    goToPage(n) {
      if (n < 1 || n > this.pageMeta.last_page || n === this.pageMeta.current_page) {
        return;
      }
      this.page = n;
      this.fetchItem();
    },
    prevPage() {
      this.goToPage(this.pageMeta.current_page - 1);
    },
    nextPage() {
      this.goToPage(this.pageMeta.current_page + 1);
    },
    onPerPageChange() {
      this.page = 1;
      this.fetchItem();
    },

    sortByColumn(sortByKey) {
      const current = this.sortOption;
      let nextDir = "asc";
      if (current && current.sort_by === sortByKey && current.sort === "asc") {
        nextDir = "desc";
      }
      const match = this.sortOptions.find(
        (o) => o.sort_by === sortByKey && o.sort === nextDir
      );
      if (match) this.sortOption = match;
    },
    columnArrow(sortByKey) {
      if (!this.sortOption || this.sortOption.sort_by !== sortByKey) return "";
      return this.sortOption.sort === "asc" ? "↑" : "↓";
    },

    productSize(val) {
      if (
        val != null &&
        val.stock != null &&
        val.size != null &&
        val.size.unit != null
      ) {
        if (val.size.unit === "pack") {
          const num = val.stock / val.size.amount;
          return `${val.stock} / ${Number(num).toFixed(1)}`;
        }
        return val.stock;
      }
      if (
        val != null &&
        val.stock != null &&
        val.product_id != null &&
        val.product_id.size != null &&
        val.product_id.size.unit != null
      ) {
        if (val.product_id.size.unit === "pack") {
          const num = val.stock / val.product_id.size.amount;
          return `${val.stock} / ${Number(num).toFixed(1)}`;
        }
        return val.stock;
      }
      return val?.stock;
    },
    getProductTypeColor(productType) {
      switch (productType) {
        case "variant":
          return "#2196f3";
        case "bundle":
          return "#9c27b0";
        case "no_stock_track":
          return "#ff9800";
        case "regular":
        default:
          return "#4caf50";
      }
    },
    stockHealth(item) {
      const stock = Number(item?.stock) || 0;
      if (stock === 0) return { label: this.$t("out_of_stock"), tone: "out", pct: 0 };
      if (stock <= 10) return { label: this.$t("low_stock"), tone: "low", pct: Math.max(8, stock) };
      return { label: this.$t("in_stock"), tone: "ok", pct: Math.min(100, stock) };
    },
    stockBarWidth(item) {
      const pct = this.stockHealth(item).pct;
      return `${Math.min(100, pct)}%`;
    },
    rowMargin(item) {
      const cost = Number(item?.price_buy) || 0;
      const price = Number(item?.price_sale) || 0;
      if (!price) return this.$t("no_price");
      const margin = ((price - cost) / price) * 100;
      return `${margin.toFixed(0)}% ${this.$t("margin")}`;
    },
    hexToRgba(hex, alpha) {
      const h = (hex || "#8c9eff").replace("#", "");
      const full =
        h.length === 3
          ? h
              .split("")
              .map((c) => c + c)
              .join("")
          : h;
      const num = parseInt(full, 16);
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    kpiIconStyle(item) {
      return {
        background: this.hexToRgba(item.color, 0.14),
        color: item.color,
        borderColor: this.hexToRgba(item.color, 0.4),
      };
    },
  },
};
</script>

<style scoped>
.ps-products {
  --ps-pr-ink: #141021;
  --ps-pr-muted: #64748b;
  --ps-pr-faint: #94a3b8;
  --ps-pr-accent: #5b39c9;
  --ps-pr-accent-soft: #7c5cea;
  --ps-pr-card-bg: #ffffff;
  --ps-pr-card-border: #e6e8f4;
  --ps-pr-line: #eef0f6;
  --ps-pr-line-strong: #e2e8f0;
  --ps-pr-track: #eef0f6;
  --ps-pr-row-hover: rgba(91, 57, 201, 0.05);
  --ps-pr-thumb-bg: #f1f5f9;
  --ps-pr-control-bg: #ffffff;
  --ps-pr-price: #475569;
  --ps-pr-pill-active-bg: rgba(91, 57, 201, 0.16);
  --ps-pr-pill-active-border: rgba(91, 57, 201, 0.35);
  --ps-pr-danger: #dc2626;
}

.theme--dark .ps-products {
  --ps-pr-ink: #eef0fa;
  --ps-pr-muted: #9aa1bb;
  --ps-pr-faint: #7c82a0;
  --ps-pr-accent: #9a8cf0;
  --ps-pr-accent-soft: #b3a8f5;
  --ps-pr-card-bg: #1b1d30;
  --ps-pr-card-border: rgba(154, 140, 240, 0.16);
  --ps-pr-line: rgba(148, 154, 184, 0.12);
  --ps-pr-line-strong: rgba(154, 140, 240, 0.22);
  --ps-pr-track: rgba(154, 140, 240, 0.14);
  --ps-pr-row-hover: rgba(154, 140, 240, 0.08);
  --ps-pr-thumb-bg: rgba(154, 140, 240, 0.12);
  --ps-pr-control-bg: #22243a;
  --ps-pr-price: #c3c8de;
  --ps-pr-pill-active-bg: rgba(154, 140, 240, 0.2);
  --ps-pr-pill-active-border: rgba(154, 140, 240, 0.45);
  --ps-pr-danger: #f87171;
}

/* ---------- KPI summary cards (Matches /report-bill) ---------- */

.ps-kpi-currency {
  margin-left: auto;
}

.ps-card-soft {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  box-shadow: none !important;
  transition: box-shadow 0.15s ease;
}

.theme--dark .ps-card-soft {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* ---------- Table card head ---------- */

.ps-table-card {
  border-radius: 18px !important;
}

.ps-table-head {
  gap: 10px;
  margin-bottom: 12px;
}

.ps-title-tick {
  width: 3px;
  height: 16px;
  border-radius: 2px;
  background: var(--ps-pr-accent);
  display: inline-block;
  margin-right: 10px;
}

.ps-table-title {
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: var(--ps-pr-ink);
}

.ps-count-note {
  margin-left: 10px;
  font-size: 11.5px;
  color: var(--ps-pr-faint);
}

.ps-status-toggle {
  border: 1px solid #e2e8f0;
  border-radius: 10px !important;
  background: #f1f2f7 !important;
  padding: 3px;
  height: 38px !important;
  gap: 4px;
  display: inline-flex;
  align-items: center;
  box-shadow: none !important;
}

.ps-status-toggle .v-btn {
  text-transform: none;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background-color: transparent !important;
  border: none !important;
  height: 30px !important;
  padding: 0 12px;
  border-radius: 7px !important;
  box-shadow: none !important;
}

.ps-status-toggle .v-btn.v-btn--active {
  background-color: #ffffff !important;
  color: #5b39c9 !important;
  border: none !important;
  border-radius: 7px !important;
  font-weight: 700 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06) !important;
}

.ps-products--dark .ps-status-toggle {
  border-color: rgba(154, 140, 240, 0.16);
  background: #1b1d30 !important;
}

.ps-products--dark .ps-status-toggle .v-btn {
  color: #7c82a0;
}

.ps-products--dark .ps-status-toggle .v-btn.v-btn--active {
  background-color: rgba(154, 140, 240, 0.18) !important;
  color: #c4b5fd !important;
  box-shadow: none !important;
}

.ps-status-toggle .v-btn.v-btn--active::before {
  opacity: 0 !important;
}

.ps-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
  color: #64748b;
  background: rgba(148, 163, 184, 0.18);
}

.ps-status-toggle .v-btn.v-btn--active .ps-status-badge {
  color: #5b39c9;
  background: rgba(91, 57, 201, 0.14);
}

.ps-products--dark .ps-status-badge {
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.08);
}

.ps-products--dark .ps-status-toggle .v-btn.v-btn--active .ps-status-badge {
  color: #c4b5fd;
  background: rgba(167, 139, 250, 0.25);
}

/* ---------- Filter bar ---------- */

.ps-filter-bar {
  gap: 10px;
  margin-bottom: 10px;
}

.ps-search-wrap {
  flex: 1 1 240px;
  max-width: 340px;
  min-width: 200px;
}

.ps-select-wrap {
  flex: 0 0 auto;
  min-width: 150px;
}

.ps-clear-filters {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ps-pr-accent);
  cursor: pointer;
  white-space: nowrap;
}
.ps-clear-filters:hover {
  text-decoration: underline;
}

.ps-action-group {
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 0;
}

.ps-icon-btn {
  border-color: var(--ps-pr-card-border) !important;
  color: var(--ps-pr-muted) !important;
}

.ps-export-btn {
  text-transform: none;
  font-weight: 600;
  border-color: var(--ps-pr-card-border) !important;
  color: var(--ps-pr-muted) !important;
  background: var(--ps-pr-control-bg) !important;
}

.ps-new-btn {
  text-transform: none;
  font-weight: 600;
  background: var(--ps-pr-accent) !important;
  color: #ffffff !important;
}

/* ---------- Table ---------- */

.ps-table-wrap {
  min-width: 0;
  overflow-x: auto;
}

.ps-grid-row {
  display: grid;
  grid-template-columns:
    40px 40px minmax(150px, 1.5fr) 104px minmax(126px, 1fr)
    minmax(110px, 0.9fr) minmax(122px, 1fr) 62px 96px 96px;
  align-items: center;
  gap: 10px;
  min-width: 980px;
}

.ps-head-row {
  padding: 0 6px 9px;
  border-bottom: 1px solid var(--ps-pr-line-strong);
  position: sticky;
  top: -1px;
  z-index: 3;
  background: var(--ps-pr-card-bg);
}

.ps-th {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ps-pr-faint);
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}

.ps-th--sortable {
  cursor: pointer;
}

.ps-th--right {
  justify-content: flex-end;
  text-align: right;
}

.ps-th--center {
  justify-content: center;
  text-align: center;
}

.ps-th-arrow {
  font-size: 10px;
  color: var(--ps-pr-accent);
}

.ps-row {
  padding: 9px 6px;
  border-bottom: 1px solid var(--ps-pr-line);
  transition: background-color 0.15s ease, opacity 0.15s ease;
}

.ps-row:last-child {
  border-bottom: none;
}

.ps-row:hover {
  background: var(--ps-pr-row-hover);
}

.ps-row.is-inactive {
  opacity: 0.55;
}

.ps-row-index {
  font-size: 12.5px;
  color: var(--ps-pr-faint);
  font-variant-numeric: tabular-nums;
}

.ps-row-thumb {
  position: relative;
  width: 32px;
  height: 32px;
  display: inline-flex;
}

.ps-thumb-avatar {
  background: var(--ps-pr-thumb-bg) !important;
}

.ps-thumb-dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid var(--ps-pr-card-bg);
}

.ps-row-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ps-product-name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ps-pr-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-product-code {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  color: var(--ps-pr-faint);
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-copy-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 0;
  background: transparent;
  color: var(--ps-pr-faint);
  cursor: pointer;
}

.ps-copy-code:hover {
  color: var(--ps-pr-accent);
}

.ps-category-pill {
  font-size: 11.5px;
  padding: 3px 8px;
  border-radius: 999px;
  width: fit-content;
  color: var(--ps-pr-muted);
  background: var(--ps-pr-track);
  border: 1px solid var(--ps-pr-card-border);
  white-space: nowrap;
}

.ps-row-stock {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.ps-stock-value {
  font-size: 13.5px;
  font-variant-numeric: tabular-nums;
  color: var(--ps-pr-ink);
}

.ps-stock-value--low {
  color: var(--ps-pr-accent);
}

.ps-stock-value--out {
  color: var(--ps-pr-faint);
}

.ps-stock-note {
  font-size: 10.5px;
  color: var(--ps-pr-faint);
}

.ps-stock-track {
  height: 4px;
  border-radius: 999px;
  background: var(--ps-pr-track);
  overflow: hidden;
  display: block;
}

.ps-stock-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
}

.ps-stock-fill--ok {
  background: linear-gradient(to right, var(--ps-pr-accent-soft), var(--ps-pr-accent));
}

.ps-stock-fill--low {
  background: var(--ps-pr-accent);
}

.ps-stock-fill--out {
  background: var(--ps-pr-card-border);
}

.ps-cost {
  text-align: right;
  font-size: 13px;
  color: var(--ps-pr-price);
  font-variant-numeric: tabular-nums;
}

.ps-row-price {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: flex-end;
}

.ps-price-value {
  font-size: 13.5px;
  font-variant-numeric: tabular-nums;
  color: var(--ps-pr-ink);
}

.ps-margin-note {
  font-size: 10.5px;
  color: var(--ps-pr-accent);
}

.ps-row-status {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ps-switch ::v-deep .v-input--selection-controls__input {
  transform: scale(0.85);
}

.ps-row-date {
  text-align: right;
  font-size: 12.5px;
  color: var(--ps-pr-faint);
  font-variant-numeric: tabular-nums;
}

.ps-row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.ps-act {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  cursor: pointer;
  color: var(--ps-pr-muted);
  border: 1px solid var(--ps-pr-card-border);
}

.ps-act--danger {
  color: var(--ps-pr-danger);
}

.ps-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 46px 20px;
}

.ps-empty-icon {
  color: var(--ps-pr-faint) !important;
}

.ps-empty-text {
  font-size: 13.5px;
  color: var(--ps-pr-muted);
}

/* ---------- Pagination ---------- */

.ps-pagination {
  gap: 14px;
  padding: 12px 2px 2px;
}

.ps-page-note {
  font-size: 12px;
  color: var(--ps-pr-faint);
}

.ps-per-page {
  gap: 8px;
  font-size: 12px;
  color: var(--ps-pr-faint);
}

.ps-per-page-select select {
  background: var(--ps-pr-control-bg);
  border: 1px solid var(--ps-pr-card-border);
  border-radius: 6px;
  padding: 5px 8px;
  color: var(--ps-pr-ink);
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
}

.ps-pager {
  gap: 5px;
}

.ps-pager-btn,
.ps-pager-num {
  min-width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  font-size: 12.5px;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
  color: var(--ps-pr-muted);
  border: 1px solid var(--ps-pr-card-border);
}

.ps-pager-num.is-active {
  color: var(--ps-pr-accent);
  background: var(--ps-pr-pill-active-bg);
  border-color: var(--ps-pr-pill-active-border);
}

.ps-pager-btn.is-disabled {
  color: var(--ps-pr-card-border);
  cursor: default;
}
</style>
